import { CapabilityBroker } from "../host/broker.js";
import { APPS, findApp } from "../host/registry.js";
import { el, setAttr, setText } from "../lib/dom.js";
import { reconcileKeyed } from "../lib/keyed.js";
import { effect, signal } from "../lib/signal.js";
import { createIcon } from "../render/icon.js";
import { createTerminalSurface } from "../render/terminal.js";
import type { SurfaceFactory } from "../render/surfaces.js";
import { controlPlane, type SpriteStatus } from "../sprite/control.js";
import { openSpritePty, type SpriteTarget } from "../sprite/pty.js";
import { currentRoomId, setCurrentRoomId } from "../workspace/location.js";
import type { Workspace } from "../workspace/open.js";
import {
  closeWindow,
  createRoom,
  flake,
  deleteRoom,
  type Geometry,
  type Layout,
  openWindow,
  renameRoom,
  setGeometry,
  setLayout,
  setTitle,
  swapTiles,
  type WindowSnapshot,
} from "../workspace/schema.js";
import { ObjectStore } from "../workspace/objects.js";
import { Powerbox } from "./Powerbox.js";
import { RoomBar } from "./RoomBar.js";
import css from "./Shell.module.css";
import { tiled, windowAtPoint } from "./tiles.js";
import { WindowFrame } from "./WindowFrame.js";

const LAYOUTS = ["floating", "tiles"] as const;

/** Shown in the bar so a screenshot says which build it is. */
const PHASE = "phase 4";

export function mountShell(workspace: Workspace, container: HTMLElement): () => void {
  const { root } = workspace;

  const requestedRoom = signal<string | null>(currentRoomId());
  const desktopSize = signal({ w: 0, h: 0 }, (a, b) => a.w === b.w && a.h === b.h);

  // Stacking order, front-most last. Deliberately not in the workspace document:
  // which window you last touched on the laptop should not reorder the phone.
  const stack: string[] = [];
  const stackVersion = signal(0);

  const frames = new Map<string, WindowFrame>();

  // Capabilities (§5.3, §5.4). The picker is the grant; there is no permission
  // prompt anywhere in this file, by design.
  const objects = new ObjectStore({ workspaceId: workspace.id, sync: workspace.syncUrl });
  const powerbox = new Powerbox(root);

  // The workspace's environment (§4.6.2). One home Sprite per workspace,
  // provisioned from the flake in the document, and *not* waited for: it takes
  // about 77 seconds, so the workspace is fully usable without one and `exec`
  // simply is not on offer until it exists.
  const control = controlPlane(workspace.id);
  let sprite: SpriteTarget | null = null;

  const broker = new CapabilityBroker({
    ws: root,
    objects,
    pick: (request) => powerbox.pick(request),
    // A closure, not a value: the Sprite may arrive long after the broker.
    openPty: (spec) => {
      if (sprite === null) throw new Error("this workspace has no environment yet");
      return openSpritePty(sprite, spec);
    },
  });

  const stopEnvironment = trackEnvironment();
  let placed: { window: WindowSnapshot; rect: Geometry }[] = [];
  let activeRoomId: string | null = null;

  /* ------------------------------------------------------------- chrome */

  const roomBar = new RoomBar({
    select: (id) => requestedRoom.set(id),
    rename: (id, name) => renameRoom(root, id, name),
    create: () => {
      const count = workspace.snapshot.peek().rooms.length;
      requestedRoom.set(createRoom(root, `Room ${count + 1}`, Date.now()));
    },
    remove: (id) => {
      deleteRoom(root, id);
      requestedRoom.set(null);
    },
  });

  const layoutButtons = LAYOUTS.map((option) => {
    const button = el(
      "button",
      { type: "button", class: css.segment ?? "", "data-layout-option": option },
      [createIcon(option === "floating" ? "app" : "collection", 12), option],
    );
    button.addEventListener("click", () => {
      if (activeRoomId !== null) setLayout(root, activeRoomId, option);
    });
    return button;
  });

  const launchButtons = APPS.map((app) => {
    const button = el(
      "button",
      { type: "button", class: css.launch ?? "", "data-launch": app.id },
      [createIcon("plus", 13), app.name],
    );
    button.addEventListener("click", () => {
      if (activeRoomId === null) return;
      openWindow(root, activeRoomId, {
        appId: app.id,
        title: app.name,
        size: app.defaultSize,
      });
    });
    return button;
  });

  const status = el("span", { class: css.status ?? "", "data-role": "window-count" });

  // Whether this device is talking to the relay. Worth showing: the workspace
  // works the same either way, so without an indicator "offline" is invisible.
  const syncDot = el("span", { class: css.syncDot ?? "" });
  const syncLabel = el("span", {});
  const syncStatus = el(
    "div",
    { class: css.sync ?? "", "data-role": "sync-status", "data-status": "local" },
    [syncDot, syncLabel],
  );
  const provider = workspace.sync;
  const disposeSync =
    provider === null
      ? (setText(syncLabel, "local"), () => undefined)
      : effect(() => {
          const state = provider.status.get();
          setAttr(syncStatus, "data-status", state);
          // How many times the relay's log has been merged. Observable because
          // "connected" and "has actually received anything" are different
          // states, and only the second one means the device is up to date.
          setAttr(syncStatus, "data-synced", String(provider.synced.get()));
          setText(syncLabel, state);
        });

  const bar = el("header", { class: css.bar ?? "" }, [
    el("div", { class: css.brand ?? "" }, [
      el("span", { class: css.brandMark ?? "" }, ["◍"]),
      el("span", {}, ["gelatOS"]),
    ]),
    el("span", { class: css.phase ?? "" }, [PHASE]),
    el("div", { class: css.rule ?? "" }),
    roomBar.el,
    el("div", { class: css.spacer ?? "" }),
    el("div", { class: css.segmented ?? "", role: "group", "aria-label": "Layout" }, layoutButtons),
    ...launchButtons,
    syncStatus,
    status,
  ]);

  const emptyTitle = el("span", { class: css.emptyTitle ?? "" });
  const empty = el("div", { class: css.empty ?? "", hidden: true }, [
    emptyTitle,
    el("span", { class: css.emptyHint ?? "" }, ["launch an app from the bar"]),
  ]);
  // Window frames live in their own layer so the keyed reconciler owns every
  // child of its host and nothing else shares that parent.
  const layer = el("div", { class: css.layer ?? "" });
  const desktop = el("main", { class: css.desktop ?? "", "data-role": "desktop" }, [empty, layer]);
  const shell = el("div", { class: css.shell ?? "" }, [bar, desktop, powerbox.el]);
  container.appendChild(shell);

  const resizeObserver = new ResizeObserver(([entry]) => {
    if (entry === undefined) return;
    desktopSize.set({ w: entry.contentRect.width, h: entry.contentRect.height });
  });
  resizeObserver.observe(desktop);

  /* ----------------------------------------------------------- stacking */

  const syncStack = (ids: readonly string[]): void => {
    for (let i = stack.length - 1; i >= 0; i--) {
      const id = stack[i];
      if (id !== undefined && !ids.includes(id)) stack.splice(i, 1);
    }
    for (const id of ids) if (!stack.includes(id)) stack.push(id);
  };

  const focusWindow = (id: string): void => {
    const index = stack.indexOf(id);
    if (index < 0 || index === stack.length - 1) return;
    stack.splice(index, 1);
    stack.push(id);
    stackVersion.set(stackVersion.peek() + 1);
  };

  /**
   * An `ObjectRef` was dropped on a window. The capability id names an object;
   * the receiving window gets its *own* fresh port to it. Nothing about the
   * object's content passes through the drag.
   */
  const handOver = async (windowId: string, capabilityId: string): Promise<void> => {
    const objectId = broker.targetOf(capabilityId);
    if (objectId === null) return;
    const granted = await broker.objectCapability(windowId, objectId);
    if (granted !== null) frames.get(windowId)?.grant(granted);
  };

  const tileDrop = (windowId: string, clientX: number, clientY: number): void => {
    if (activeRoomId === null) return;
    const bounds = layer.getBoundingClientRect();
    const target = windowAtPoint(placed, clientX - bounds.x, clientY - bounds.y);
    if (target !== null) swapTiles(root, activeRoomId, windowId, target.id);
  };

  /* -------------------------------------------------------------- render */

  const dispose = effect(() => {
    const snapshot = workspace.snapshot.get();
    const requested = requestedRoom.get();
    const size = desktopSize.get();
    stackVersion.get();

    const room = snapshot.rooms.find((entry) => entry.id === requested) ?? snapshot.rooms[0] ?? null;
    activeRoomId = room?.id ?? null;
    if (activeRoomId !== null) setCurrentRoomId(activeRoomId);

    const layout: Layout = room?.layout ?? "floating";
    const windows = room?.windows ?? [];

    roomBar.update(snapshot.rooms, activeRoomId);
    for (const button of layoutButtons) {
      setAttr(button, "data-active", String(button.dataset["layoutOption"] === layout));
      button.disabled = activeRoomId === null;
    }
    for (const button of launchButtons) button.disabled = activeRoomId === null;
    setText(status, `${windows.length} window${windows.length === 1 ? "" : "s"}`);
    setAttr(desktop, "data-layout", layout);
    empty.hidden = windows.length > 0;
    setText(emptyTitle, `Nothing open in ${room?.name ?? "this room"}`);

    placed =
      layout === "tiles"
        ? tiled(windows, size)
        : windows.map((window) => ({ window, rect: window.geometry }));

    syncStack(windows.map((window) => window.id));

    reconcileKeyed(layer, placed, frames, {
      key: (entry) => entry.window.id,
      create: (entry) =>
        new WindowFrame(entry.window, findApp(entry.window.appId), {
          focus: () => focusWindow(entry.window.id),
          close: () => {
            if (activeRoomId !== null) closeWindow(root, activeRoomId, entry.window.id);
          },
          setGeometry: (geometry) => {
            if (activeRoomId !== null) setGeometry(root, activeRoomId, entry.window.id, geometry);
          },
          setTitle: (title) => {
            if (activeRoomId !== null) setTitle(root, activeRoomId, entry.window.id, title);
          },
          tileDrop: (x, y) => tileDrop(entry.window.id, x, y),
          // Re-minted on every mount, including a restart, so a restarted app
          // never inherits the previous instance's authority.
          capabilities: () => {
            broker.revokeWindow(entry.window.id);
            return [broker.privateCollection(entry.window.id, entry.window.appId)];
          },
          request: (request) =>
            broker.request({
              windowId: entry.window.id,
              appId: entry.window.appId,
              kind: request.kind,
              filter: request.filter,
            }),
          objectDropped: (capabilityId) => {
            void handOver(entry.window.id, capabilityId);
          },
          // Window-scoped by construction: a `Surface` can only ever resolve a
          // capability this window was granted (§11).
          surfaces: surfacesFor(entry.window.id),
        }),
      update: (frame, entry) => {
        const index = stack.indexOf(entry.window.id);
        frame.update(
          entry.window,
          entry.rect,
          layout,
          stack[stack.length - 1] === entry.window.id,
          index < 0 ? 1 : index + 1,
        );
      },
      destroy: (frame) => {
        // Closing a window revokes its capabilities. The app cannot read the
        // object afterwards, because the port it was reading through is gone.
        broker.revokeWindow(frame.windowId);
        frame.destroy();
      },
    });
  });

  function surfacesFor(windowId: string): SurfaceFactory {
    return (kind, host) => {
      if (kind !== "terminal") return null;
      return createTerminalSurface(host, (capabilityId) =>
        broker.streamSession(windowId, capabilityId),
      );
    };
  }

  /**
   * Ask for the workspace's home Sprite, and keep asking while it builds.
   *
   * Polling rather than a socket on purpose: this is a once-per-workspace,
   * once-per-77-seconds question, and a second live connection to maintain for
   * it would cost more than it saves.
   */
  function trackEnvironment(): () => void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const apply = (status: SpriteStatus): void => {
      if (status.state === "ready") {
        sprite = status.target;
        powerbox.setEnvironments([{ name: status.name, detail: "home" }]);
        return;
      }
      sprite = null;
      powerbox.setEnvironments([]);
    };

    const poll = async (): Promise<void> => {
      if (stopped) return;
      try {
        const status = await control.get();
        apply(status);
        if (status.state === "ready") return;
        if (status.state === "none" || status.state === "failed") {
          apply(await control.ensure(flake(root)));
        }
      } catch {
        // Offline, or no control plane in this deployment. The workspace works
        // without one; only `exec` does not.
      }
      if (!stopped) timer = setTimeout(() => void poll(), 5_000);
    };

    void poll();
    return () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
    };
  }

  return () => {
    stopEnvironment();
    dispose();
    disposeSync();
    resizeObserver.disconnect();
    broker.destroy();
    objects.destroy();
    for (const frame of frames.values()) frame.destroy();
    frames.clear();
    shell.remove();
  };
}
